import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import Switcher from "../../components/switcher";

import logoDark from '../../assets/images/logo-dark.svg'
import logoLight from '../../assets/images/logo-light.svg'

import {FiLink, FiMapPin, FiMail, FiPhone} from '../../assets/icons/vander'
import { fetchPurchase, fetchPackage } from "../../services/api";

const formatMoney = (amountCents = 0, currency = "CLP") => {
    // Los montos CLP se almacenan en pesos (sin centavos): no se dividen por 100.
    // Solo las monedas extranjeras vienen en la subunidad (centavos).
    const amount = currency === "CLP" ? amountCents : amountCents / 100;
    return amount.toLocaleString(currency === "CLP" ? "es-CL" : "en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: currency === "CLP" ? 0 : 2,
    });
};

export default function UserInvoice(){
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const purchaseFromState = location.state?.purchase;
    const [purchase, setPurchase] = useState(purchaseFromState || null);
    const [pkg, setPkg] = useState(null);
    const [loading, setLoading] = useState(!purchaseFromState);
    const [error, setError] = useState("");
    let [modal, setModal] = useState(false);

    // Referencia = buy_order (secreto del comprador), no el id secuencial.
    const purchaseRef = purchaseFromState?.buy_order || searchParams.get("ref");

    useEffect(() => {
        if (purchaseFromState) {
            setPurchase(purchaseFromState);
            return;
        }
        if (!purchaseRef) return;
        setLoading(true);
        fetchPurchase(purchaseRef)
            .then((data) => {
                setPurchase(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message || "No se pudo cargar el comprobante.");
                setLoading(false);
            });
    }, [purchaseRef, purchaseFromState]);

    useEffect(() => {
        if (purchase?.package_id) {
            fetchPackage(purchase.package_id)
                .then((data) => setPkg(data))
                .catch(() => {});
        }
    }, [purchase]);

    // Cierre del modal de mapa con la tecla Escape.
    useEffect(() => {
        if (!modal) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setModal(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [modal]);

    const qrUrl = useMemo(() => {
        const url = `${window.location.origin}/user-invoice?ref=${encodeURIComponent(purchaseRef || "")}`;
        return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}`;
    }, [purchaseRef]);

    const rows = useMemo(() => {
        if (!purchase) return [];
        const title = pkg?.title || "Reserva";
        return [
            {
                name: title,
                qty: purchase.quantity || 1,
                rate: formatMoney(Math.round(purchase.amount_cents / (purchase.deposit_percent || 100) * 100) || purchase.amount_cents, purchase.currency),
                total: formatMoney(purchase.amount_cents, purchase.currency),
            },
        ];
    }, [purchase, pkg]);

    return(
        <>
        <Navbar navclass="defaultscroll is-sticky" navlight={false} manuclass="justify-end"/>
        <section className="relative lg:py-24 py-16 bg-slate-50 dark:bg-slate-800">
            <div className="container relative">
                <div className="md:flex justify-center mt-24">
                    <div className="lg:w-4/5 w-full">
                        <div className="p-6 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900">
                            {loading && <p className="text-slate-500">Cargando comprobante...</p>}
                            {error && <p className="text-red-500">{error}</p>}
                            {!loading && !error && purchase && (
                            <>
                            <div className="border-b border-gray-100 dark:border-gray-700 pb-6">
                                <div className="md:flex justify-between">
                                    <div>
                                        <img src={logoDark} className="block dark:hidden" alt=""/>
                                        <img src={logoLight} className="hidden dark:block" alt=""/>
                                        <div className="flex mt-4">
                                            <FiLink className="size-4 me-3 mt-1"></FiLink>
                                            <Link to="#" target="_blank" className="text-red-500 dark:text-white font-medium">www.chile-turismo.cl</Link>
                                        </div>
                                    </div>

                                    <div className="mt-6 md:mt-0 md:w-56">
                                        <h5 className="text-lg font-semibold">Dirección:</h5>

                                        <ul className="list-none">
                                            <li className="flex mt-3">
                                                <FiMapPin className="size-4 me-3 mt-1"></FiMapPin>
                                                <Link to="#" onClick={()=>setModal(!modal)} className="lightbox text-slate-400">Santiago, Chile</Link>
                                            </li>
                                            
                                            <li className="flex mt-3">
                                                <FiMail className="size-4 me-3 mt-1"></FiMail>
                                                <Link to="mailto:turismo@cmsystem.cl" className="text-slate-400">turismo@cmsystem.cl</Link>
                                            </li>
                                            
                                            <li className="flex mt-3">
                                                <FiPhone className="size-4 me-3 mt-1"></FiPhone>
                                                <Link to="tel:+56966281145" className="text-slate-400">+56 9 6628 1145</Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="md:flex justify-between">
                                <div className="mt-6">
                                    <h5 className="text-lg font-semibold">Detalles del comprobante :</h5>

                                    <ul className="list-none">
                                        <li className="flex mt-3">
                                            <span className="w-24">N° comprobante :</span>
                                            <span className="text-slate-400 tabular-nums">{purchase.id}</span>
                                        </li>

                                        <li className="flex mt-3">
                                            <span className="w-24">Nombre :</span>
                                            <span className="text-slate-400">{purchase.buyer_name || "-"}</span>
                                        </li>

                                        <li className="flex mt-3">
                                            <span className="w-24">Dirección :</span>
                                            <span className="text-slate-400">Chile</span>
                                        </li>

                                        <li className="flex mt-3">
                                            <span className="w-24">Teléfono :</span>
                                            <span className="text-slate-400 tabular-nums">+56 9 6628 1145</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="mt-3 md:w-56">
                                    <ul className="list-none">
                                        <li className="flex mt-3">
                                            <span className="w-24">Fecha :</span>
                                            <span className="text-slate-400">{purchase.transaction_date ? new Date(purchase.transaction_date).toLocaleString('es-CL') : new Date(purchase.created_at).toLocaleString('es-CL')}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="relative overflow-x-auto shadow dark:shadow-gray-800 rounded-md mt-6">
                                <table className="w-full text-start text-slate-500 dark:text-slate-400">
                                    <thead className="text-sm uppercase bg-slate-50 dark:bg-slate-800">
                                        <tr>
                                            <th scope="col" className="text-center px-6 py-3 w-16">
                                                N°
                                            </th>
                                            <th scope="col" className="text-start px-6 py-3">
                                                Ítems
                                            </th>
                                            <th scope="col" className="text-center px-6 py-3 w-20">
                                                Cant.
                                            </th>
                                            <th scope="col" className="text-center px-6 py-3 w-28">
                                                Precio
                                            </th>
                                            <th scope="col" className="text-end px-6 py-3 w-20">
                                                Total
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, idx)=> (
                                            <tr className="bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-gray-700" key={idx}>
                                                <td className="text-center px-6 py-4 tabular-nums">
                                                    {idx + 1}
                                                </td>
                                                <th scope="row" className="text-start px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                                    {row.name}
                                                </th>
                                                <td className="text-center px-6 py-4 tabular-nums">
                                                    {row.qty}
                                                </td>
                                                <td className="text-center px-6 py-4 tabular-nums">
                                                    {row.rate}
                                                </td>
                                                <td className="text-end px-6 py-4 tabular-nums">
                                                    {row.total}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="w-56 ms-auto p-5">
                                <ul className="list-none">
                                    <li className="text-slate-400 flex justify-between">
                                        <span>Subtotal :</span>
                                        <span className="tabular-nums">{rows[0]?.total || formatMoney(purchase.amount_cents, purchase.currency)}</span>
                                    </li>
                                    <li className="flex justify-between font-semibold mt-2 text-lg">
                                        <span>Total pagado :</span>
                                        <span className="tabular-nums">{formatMoney(purchase.amount_cents, purchase.currency)}</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-6 pb-4">
                                <div className="text-slate-400 text-sm">
                                    <p className="mb-1">Compra #{purchase.buy_order}</p>
                                    <p className="mb-1">Token: {purchase.token_ws}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <img src={qrUrl} alt="QR" className="w-32 h-32" />
                                    <p className="text-xs text-slate-500">Escanea para volver a este comprobante</p>
                                </div>
                            </div>

                            <div className="invoice-footer border-t border-gray-100 dark:border-gray-700 pt-6">
                                <div className="md:flex justify-between">
                                    <div>
                                        <div className="text-slate-400 text-center md:text-start">
                                            <h6 className="mb-0">Atención al cliente : <Link to="tel:+56966281145" className="text-amber-500 tabular-nums">+56 9 6628 1145</Link></h6>
                                        </div>
                                    </div>

                                    <div className="mt-4 md:mt-0">
                                        <div className="text-slate-400 text-center md:text-end">
                                            <h6 className="mb-0"><Link to="/terms" target="_blank" className="text-red-500">Términos y condiciones</Link></h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <Footer/>
        <Switcher/>
        {modal && (
            <div role="dialog" aria-modal="true" aria-label="Mapa de ubicación" style={{ overscrollBehavior: "contain" }} className="w-full h-screen bg-slate-900/80 fixed top-0 left-0 bottom-0 right-0 z-[999] flex items-center justify-center">
                <div className="w-full h-full px-5 md:px-40 md-py-20 py-5">
                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3328.627535823514!2d-70.64724022457778!3d-33.45758419741295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662c59f38f89e7d%3A0xd18ee466f229c3b7!2sSantiago!5e0!3m2!1ses-419!2scl!4v1712444400000!5m2!1ses-419!2scl" width="100%" height="100%" title="Mapa de ubicación" loading="lazy"></iframe>
                </div>
                <button aria-label="Cerrar" className="text-slate-400 absolute top-[20px] right-[20px]" onClick={()=>setModal(!modal)}>
                    <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="size-5" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
            </div>
        )}
        </>
    )
}
